import { initializeApp, getApps, getApp, FirebaseApp } from "firebase/app";
import {
  Firestore,
  getFirestore,
  connectFirestoreEmulator,
} from "firebase/firestore";
import {
  Database,
  getDatabase,
  connectDatabaseEmulator,
} from "firebase/database";
import {
  Messaging,
  getMessaging,
  getToken,
  onMessage,
} from "firebase/messaging";
import {
  Auth,
  getAuth,
  GoogleAuthProvider,
  connectAuthEmulator,
  browserLocalPersistence,
  inMemoryPersistence,
} from "firebase/auth";
import {
  FirebaseStorage,
  getStorage,
  connectStorageEmulator,
} from "firebase/storage";
import { registerFCMToken } from "@/app/actions/notification-actions";

// Only initialize Firebase if we have the required environment variables
const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  databaseURL: process.env.NEXT_PUBLIC_FIREBASE_DATABASE_URL, // For Realtime Database
};

// Check if we're in the browser and if the API key exists
const isClient = typeof window !== "undefined";
const hasRequiredConfig = !!firebaseConfig.apiKey;
const USE_EMULATORS = false; // Set to true when using Firebase emulators

// Initialize Firebase
let app: FirebaseApp | undefined;
let db: Firestore | undefined;
let rtdb: Database | undefined;
let messaging: Messaging | undefined;
let auth: Auth | undefined;
let googleProvider: GoogleAuthProvider | undefined;
let storage: FirebaseStorage | undefined;

// Initialize Firebase app first
if (hasRequiredConfig) {
  try {
    app = !getApps().length ? initializeApp(firebaseConfig) : getApp();

    if (app) {
      try {
        auth = getAuth(app);

        // Connect to auth emulator if enabled
        if (USE_EMULATORS && auth && typeof window !== "undefined") {
          connectAuthEmulator(auth, "http://localhost:9099", {
            disableWarnings: true,
          });
        } else if (auth) {
          // Ensure persistence is set for production to help with session management
          // This helps ensure that the auth state is more reliably loaded across page refreshes
          // and might help with RTDB permissions if they rely on an immediately available auth state.
          auth.setPersistence(
            isClient ? browserLocalPersistence : inMemoryPersistence
          );
        }

        googleProvider = new GoogleAuthProvider();
        googleProvider.setCustomParameters({
          prompt: "select_account",
        });
      } catch (error) {
        console.error("Error initializing Firebase Auth:", error);
      }

      try {
        db = getFirestore(app);

        // Connect to Firestore emulator if enabled
        if (USE_EMULATORS && db && typeof window !== "undefined") {
          connectFirestoreEmulator(db, "localhost", 8080);
        }
      } catch (error) {
        console.error("Error initializing Firestore:", error);
      }

      // RTDB will be initialized lazily when first accessed via getRTDB()
      // This avoids unnecessary initialization for anonymous users on landing pages

      try {
        storage = getStorage(app);

        // Connect to Storage emulator if enabled
        if (USE_EMULATORS && storage && typeof window !== "undefined") {
          connectStorageEmulator(storage, "localhost", 9199);
        }
      } catch (error) {
        console.error("Error initializing Firebase Storage:", error);
      }

      // Messaging will be initialized lazily when first accessed via getMessagingInstance()
      // This avoids unnecessary initialization for anonymous users on landing pages
    }
  } catch (error) {
    console.error("Firebase initialization error:", error);
  }
} else {
  console.warn(
    "Firebase config is missing or incomplete. Firebase services will not be available."
  );
}

// Function to request notification permission and get FCM token
export async function requestNotificationPermission(userId: string) {
  console.warn(
    "[FCM Debug] requestNotificationPermission called with userId:",
    userId
  );

  // Skip in development unless explicitly enabled
  const isDevelopment = process.env.NODE_ENV === "development";
  const enableFCMInDev = process.env.NEXT_PUBLIC_ENABLE_FCM_DEV === "true";

  if (isDevelopment && !enableFCMInDev) {
    console.warn(
      "[FCM Debug] FCM disabled in development. Set NEXT_PUBLIC_ENABLE_FCM_DEV=true to enable."
    );
    return null;
  }

  console.warn("[FCM Debug] Calling getMessagingInstance...");
  const messagingInstance = getMessagingInstance();
  console.warn(
    "[FCM Debug] getMessagingInstance returned:",
    !!messagingInstance
  );

  if (!messagingInstance || !userId) {
    console.warn(
      "[FCM Debug] Firebase Messaging not available or no user ID provided. messagingInstance:",
      !!messagingInstance,
      "userId:",
      !!userId
    );
    return null;
  }

  try {
    // Check if browser supports notifications
    if (!("Notification" in window)) {
      console.warn("This browser does not support notifications");
      return null;
    }

    const permission = await Notification.requestPermission();

    if (permission === "granted") {
      // Get FCM token with VAPID key (required for token subscription)
      const vapidKey = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY;

      if (!vapidKey) {
        console.warn(
          "VAPID key not configured. FCM notifications will not work. Please set NEXT_PUBLIC_FIREBASE_VAPID_KEY environment variable."
        );
        return null;
      }

      // FCM requires the service worker to be registered before getting a token
      let swRegistration: ServiceWorkerRegistration | undefined;
      try {
        swRegistration = await navigator.serviceWorker.register(
          "/firebase-messaging-sw.js"
        );
        console.log(
          "Firebase messaging service worker registered:",
          swRegistration.scope
        );
        // Wait for the service worker to be ready
        await navigator.serviceWorker.ready;
        console.log("Service worker is ready");
      } catch (swError) {
        console.error("Failed to register firebase-messaging-sw.js:", swError);
        return null;
      }

      // Log diagnostic info
      console.log("FCM getToken - VAPID key present:", !!vapidKey);
      console.log(
        "FCM getToken - SW registration scope:",
        swRegistration?.scope
      );
      console.log(
        "FCM getToken - Messaging instance present:",
        !!messagingInstance
      );

      let token: string | null = null;
      try {
        token = await getToken(messagingInstance, {
          vapidKey: vapidKey,
          serviceWorkerRegistration: swRegistration,
        });
        console.log(
          "FCM getToken result:",
          token ? "Token received" : "No token returned"
        );
      } catch (getTokenError: any) {
        console.error("FCM getToken error:", getTokenError);
        console.error("FCM getToken error code:", getTokenError?.code);
        console.error("FCM getToken error message:", getTokenError?.message);
        return null;
      }

      if (token) {
        console.log("FCM token obtained successfully");
        // Save token to Firestore for push notification delivery
        try {
          await registerFCMToken(userId, token);
          console.log("FCM token saved to user profile");
        } catch (saveError) {
          console.error("Failed to save FCM token:", saveError);
        }
        return token;
      } else {
        console.warn(
          "Failed to get FCM token - getToken returned null. Check Firebase Console: ensure FCM API is enabled, VAPID key matches the one in Firebase Console, and the domain is authorized."
        );
      }
    } else {
      console.warn("Notification permission denied");
    }

    return null;
  } catch (error: any) {
    // Provide better diagnostic messages for common FCM errors
    if (error?.code === "messaging/token-subscribe-failed") {
      console.warn(
        "FCM subscription failed. This is expected in development without proper VAPID configuration."
      );
    } else if (error?.message?.includes("401")) {
      console.warn(
        "FCM authentication error (401). Ensure FCM API is enabled in Firebase Console and VAPID key is correctly configured."
      );
    } else {
      console.error("Error requesting notification permission:", error);
    }
    return null;
  }
}

// Lazy initialization for RTDB - only initialize when first accessed
export function getRTDB(): Database | undefined {
  if (rtdb) return rtdb;

  if (!app || !firebaseConfig.databaseURL) {
    console.warn("Firebase app or RTDB URL not configured");
    return undefined;
  }

  try {
    console.log("Lazy initializing RTDB with URL:", firebaseConfig.databaseURL);
    rtdb = getDatabase(app, firebaseConfig.databaseURL);

    // Connect to RTDB emulator if enabled
    if (USE_EMULATORS && rtdb && typeof window !== "undefined") {
      connectDatabaseEmulator(rtdb, "localhost", 9000);
    }

    return rtdb;
  } catch (error) {
    console.error("Error lazy-initializing Realtime Database:", error);
    return undefined;
  }
}

// Lazy initialization for Messaging - only initialize when first accessed
export function getMessagingInstance(): Messaging | undefined {
  console.warn(
    "[FCM Debug] getMessagingInstance called, messaging already exists:",
    !!messaging
  );
  if (messaging) return messaging;

  console.warn("[FCM Debug] app exists:", !!app, "isClient:", isClient);
  if (!app || !isClient) {
    console.warn(
      "[FCM Debug] Returning undefined - app:",
      !!app,
      "isClient:",
      isClient
    );
    return undefined;
  }

  try {
    // Skip FCM initialization in development unless explicitly enabled
    const enableFCMInDev = process.env.NEXT_PUBLIC_ENABLE_FCM_DEV === "true";
    const isDevelopment = process.env.NODE_ENV === "development";

    console.warn(
      "[FCM Debug] isDevelopment:",
      isDevelopment,
      "enableFCMInDev:",
      enableFCMInDev
    );
    if (isDevelopment && !enableFCMInDev) {
      console.log(
        "[FCM Debug] Firebase Messaging disabled in development. Set NEXT_PUBLIC_ENABLE_FCM_DEV=true to enable."
      );
      return undefined;
    }

    // Check if the browser supports Firebase Messaging
    const hasServiceWorker = "serviceWorker" in navigator;
    const hasNotification = "Notification" in window;
    const hasPushManager = "PushManager" in window;
    const notInServiceWorker =
      typeof (globalThis as any).importScripts === "undefined";

    console.warn(
      "[FCM Debug] Browser support check: serviceWorker:",
      hasServiceWorker,
      "Notification:",
      hasNotification,
      "PushManager:",
      hasPushManager,
      "notInServiceWorker:",
      notInServiceWorker
    );

    if (
      hasServiceWorker &&
      hasNotification &&
      hasPushManager &&
      notInServiceWorker
    ) {
      // Additional check for secure context (required for FCM)
      const isSecure =
        window.isSecureContext || window.location.hostname === "localhost";
      console.warn(
        "[FCM Debug] isSecureContext:",
        window.isSecureContext,
        "hostname:",
        window.location.hostname,
        "isSecure:",
        isSecure
      );

      if (isSecure) {
        messaging = getMessaging(app);
        console.warn(
          "[FCM Debug] Firebase Messaging lazy-initialized successfully"
        );
        return messaging;
      } else {
        console.warn(
          "[FCM Debug] Firebase Messaging requires HTTPS in production"
        );
      }
    } else {
      console.warn(
        "[FCM Debug] Browser doesn't support Firebase Cloud Messaging - missing one of the required APIs"
      );
    }
  } catch (error) {
    console.warn(
      "[FCM Debug] Firebase Cloud Messaging lazy-initialization failed:",
      error
    );
    messaging = undefined;
  }

  return undefined;
}

// Function to handle foreground messages
export function onForegroundMessage(callback: (payload: any) => void) {
  const messagingInstance = getMessagingInstance();
  if (!messagingInstance) return () => {};

  return onMessage(messagingInstance, (payload) => {
    callback(payload);
  });
}

// Export the initialized Firebase app and services
export { app, db, auth, googleProvider, storage };

// Export rtdb and messaging as getters for lazy loading
export { rtdb, messaging };

// Export a function to get auth - this ensures auth is only initialized when needed
export function getFirebaseAuth() {
  if (!app) return null;

  if (auth && googleProvider) {
    return { auth, googleProvider };
  }

  return null;
}
